import React, { useEffect } from 'react';
import {
  ButtonWrapper,
  Column,
  DeleteButton,
  Form,
  FreePreviewWrapper,
  LoadingContainer,
  MediaContainer,
  MediaContentContainer,
  NumberInput,
  Row,
  SubmitButton,
} from './EditBundleModal.styles';
import BaseModal from '../utils/BaseModal/BaseModal';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import Image from '../../utils/Image/Image';
import { useDispatch, useSelector } from 'react-redux';
import { deleteBundle, editBundle } from '../../../store/slices/user/asyncThunks';
import { selectUserActionsPending } from '../../../store/slices/user/slice';
import { notifyError, notifyInfo } from '../../../utils/notify';
import Spinner from '../../Spinner/Spinner';
import { useConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import { ALBUM_MEDIA_TYPE } from '../../../utils/constants';
import Video from '../../utils/Video/Video';
import SmallInput from '../../utils/inputs/SmallInput/SmallInput';
import { useNavigate } from 'react-router-dom';
import { CHATTER_VAULT } from '../../App/routes';

const editBundlesFormSchema = yup.object({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  caption: yup.string().required('Caption is required'),
  cost: yup
    .number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || Number.isNaN(value)) {
        return undefined;
      }
      return value;
    })
    .positive('Price must be positive')
    .max(500, 'Price must be at most 500')
    .notRequired(),
});

const EditBundleModal = ({ modalRef, selectedBundle, setSelectedBundle, onSuccess, influencerId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { deleteBundle: deleteBundlePending, editBundle: editBundlePending } = useSelector(selectUserActionsPending);
  const loading = editBundlePending || deleteBundlePending;

  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    delayError: 300,
    defaultValues: {
      name: '',
      description: '',
      caption: '',
      cost: '',
      media: [],
    },
    resolver: yupResolver(editBundlesFormSchema),
  });

  const mediaWatch = watch('media');

  useEffect(() => {
    if (selectedBundle) {
      setValue('name', selectedBundle.name);
      setValue('description', selectedBundle.description);
      setValue('media', selectedBundle.media);
      if (selectedBundle.cost) {
        setValue('cost', selectedBundle.cost);
      }
      if (selectedBundle.caption) {
        setValue('caption', selectedBundle.caption);
      }
    }
  }, [dispatch, selectedBundle, setValue]);

  useEffect(() => {
    setConfirmationModalLoader(deleteBundlePending);
  }, [deleteBundlePending, setConfirmationModalLoader]);

  const onSubmit = data => {
    const { cost, description, caption, name } = data;

    dispatch(
      editBundle({
        cost,
        description,
        caption,
        name,
        bundleId: selectedBundle.id,
        ...(influencerId && { influencerId }),
      }),
    )
      .unwrap()
      .then(() => {
        reset();
        modalRef.current.hide();
        onSuccess();
        notifyInfo('Bundle edited successfully!');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onDeleteBundle = () => {
    openModal(
      'Delete Bundle',
      `Are you sure? This action will permanently delete the bundle.`,
      'Delete',
      () => {
        dispatch(
          deleteBundle({
            bundleId: selectedBundle.id,
            ...(influencerId && { influencerId }),
          }),
        )
          .unwrap()
          .then(() => {
            navigate(`/${CHATTER_VAULT.replace(':influencerId', influencerId)}`);
            closeModal();
            modalRef.current.hide();
            notifyInfo('Bundle deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  return (
    <BaseModal
      title="Edit Bundle"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        setSelectedBundle(null);
        reset();
      }}>
      {!selectedBundle ? (
        <LoadingContainer>
          <Spinner width={100} height={100} />
        </LoadingContainer>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Column>
              <SmallInput
                label="Name"
                placeholder="Enter name"
                {...register('name')}
                error={errors.name?.message}
                disabled={loading}
              />
              <SmallInput
                disabled={loading}
                label="Description"
                placeholder="Enter description"
                {...register('description')}
                error={errors.description?.message}
                type="textarea"
              />
              <SmallInput
                disabled={loading}
                label="Caption"
                placeholder="Enter caption"
                {...register('caption')}
                error={errors.caption?.message}
                type="textarea"
              />
            </Column>
            <NumberInput
              disabled={loading}
              label="Price"
              error={errors.cost?.message}
              prefix="$"
              smallError
              {...register('cost', {
                valueAsNumber: true,
              })}
            />
          </Row>
          <MediaContainer>
            {selectedBundle.representativeMedia && (
              <MediaContentContainer>
                {selectedBundle.representativeMedia.type === ALBUM_MEDIA_TYPE.PHOTO ? (
                  <Image src={selectedBundle.representativeMedia.url} />
                ) : (
                  <Video src={selectedBundle.representativeMedia.url} />
                )}
                <FreePreviewWrapper>FREE PREVIEW</FreePreviewWrapper>
              </MediaContentContainer>
            )}
            {mediaWatch.map(field => {
              return (
                <MediaContentContainer key={field.id}>
                  <Image src={field.thumbnailUrl} />
                </MediaContentContainer>
              );
            })}
          </MediaContainer>
          <ButtonWrapper>
            <DeleteButton title="Delete" onClick={onDeleteBundle} type="button" disabled={loading} />
            <SubmitButton type="submit" title="Save" isLoading={loading} />
          </ButtonWrapper>
        </Form>
      )}
    </BaseModal>
  );
};

export default EditBundleModal;
